<script setup lang="ts">
import { ref, watch } from 'vue'
import { type HTMLAttributes } from 'vue'
import { SphAutoCollapse } from '@/components/base/_internal/auto-collapse/index.ts'
import SphTag from './Tag.vue'
import type { Tag } from './Tag.vue'

defineOptions({
    compatConfig: {
        MODE: 3,
    },
    inheritAttrs: false,
})

export interface TagGroup {
    class?: HTMLAttributes['class']
    collapseClass?: HTMLAttributes['class']
    collapseContentClass?: HTMLAttributes['class']
    deletable?: boolean
}

const props = withDefaults(defineProps<TagGroup>(), {
    class: '',
    collapseClass: '',
    collapseContentClass: '',
    deletable: false,
})

const emit = defineEmits<{
    click: [ Tag ]
    delete: [ Tag ]
}>()

const tags = defineModel<Tag[]>('modelValue', {
    default: () => [],
})
const show = ref(tags.value.length > 0)

watch(tags, (newTags) => {
    show.value = newTags.length > 0
})

function deleteTag(tag: Tag) {
    const index = tags.value.findIndex(t => {
        if (t.value !== undefined && tag.value !== undefined) {
            return t.value === tag.value
        }
        return t.label === tag.label
    })

    if (index !== -1) {
        tags.value.splice(index, 1)
        emit('delete', tag)
    }
}

// transition helpers
function onBeforeLeave(el: Element) {
    const htmlEl = el as HTMLElement
    const parent = htmlEl.parentElement!
    const siblings = Array.from(parent.children).filter(c => c !== el)
    if (siblings.length === 0) {
        return
    }
    const rect = htmlEl.getBoundingClientRect()
    const parentRect = parent.getBoundingClientRect()
    htmlEl.style.width = `${rect.width}px`
    htmlEl.style.left = `${rect.left - parentRect.left}px`
    htmlEl.style.top = `${rect.top - parentRect.top}px`
    htmlEl.style.position = 'absolute'
}

function onLeave(el: Element, done: () => void) {
    el.addEventListener('transitionend', done, { once: true })
}

const showContent = ref(true)
</script>

<template>
    <SphAutoCollapse
        :model-value="showContent"
        :class="props.collapseClass"
        :content-class="props.collapseContentClass"
    >
        <TransitionGroup
            name="smooth-list"
            tag="div"
            class="tag-group"
            :class="props.class"
            @before-leave="onBeforeLeave"
            @leave="onLeave"
        >
            <SphTag
                v-for="tag of tags"
                :key="tag.value || tag.label"
                :label="tag.label"
                :deletable="props.deletable || tag.deletable"
                @click="emit('click', tag)"
                @delete="deleteTag(tag)"
            />
        </TransitionGroup>
    </SphAutoCollapse>
</template>

<style scoped>
@reference '@/assets/styles/config.css';

.tag-group{
    @apply relative flex flex-wrap items-center gap-2 w-full overflow-hidden;
}

.smooth-list-enter-from {
    opacity: 0;
    transform: translate(0, 15%);
}

.smooth-list-leave-to {
    opacity: 0;
    transform: translate(0, 15%);
}

.smooth-list-enter-to,
.smooth-list-leave-from {
    opacity: 1;
    transform: translate(0, 0);
}

.smooth-list-enter-active,
.smooth-list-leave-active {
    transition: all .4s var(--ease-out-cubic);
}

.smooth-list-move{
    transition: all .4s var(--ease-out-cubic);
}
</style>
